import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';

import DcPage from '@/components/landing/dcv2/DcPage';
import { Input } from '@/components/ui/input';
import { ForumProvider } from '@/features/forum/ForumContext';
import { useForum } from '@/features/forum/useForum';
import { ForumCardSkeleton } from '@/features/forum/components/ForumCardSkeleton';
import { ThreadRow } from '@/features/forum/components/ThreadRow';
import {
  displaySpaceName,
  forumSearchTerm,
  matchesSearch,
  searchesOnServer,
} from '@/features/forum/forum-display';
import type { PostListItem } from '@/features/forum/types';
import { useServerPostSearch } from '@/features/forum/useServerPostSearch';

/**
 * Rows a server search of the archive asks for: one request across the
 * selected space (or every space), at the list routes' maximum, as the
 * portal's forum index searches.
 */
const ARCHIVE_SEARCH_LIMIT = 100;

/**
 * Every discussion, across every space.
 *
 * Rebuilt to the admin app's forum redesign (ai2aim-ems 3bb83bc): a breadcrumb,
 * a plain heading, the filters on one line, and the threads as dense rows in a
 * single card — the same list the index and a space page render, so the three
 * cannot drift apart.
 *
 * WHAT CAME OUT: a three-column grid of cards, each tinted from a rotating
 * palette of three accents. The rotation was positional — a thread's colour
 * came from where it happened to land in the array, so the same discussion
 * changed colour when a newer one was posted, and the colour said nothing
 * about the thread in the first place. The grid also made every discussion
 * look like a product tile.
 *
 * The space filter is a native select rather than the chip row that was here:
 * chips are fine for the three spaces this org has and wrap into a wall at
 * twenty, and this is the one page that exists precisely because the forum got
 * big.
 */
function ForumArchiveContent() {
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  const { state, fetchSubforums, fetchPosts, getPostsBySubforum, getSubforum } =
    useForum();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sort, setSort] = useState<'new' | 'top'>('new');

  useEffect(() => {
    if (categoryFromUrl) setActiveCategory(categoryFromUrl);
  }, [categoryFromUrl]);

  useEffect(() => {
    if (state.subforums.length === 0 && !state.loading.subforums) {
      fetchSubforums();
    }
  }, [state.subforums.length, state.loading.subforums, fetchSubforums]);

  useEffect(() => {
    state.subforums.forEach((sf) => fetchPosts(sf.id));
  }, [state.subforums, fetchPosts]);

  const allPosts = useMemo(() => {
    const rows = state.subforums.flatMap((sf) => getPostsBySubforum(sf.id));
    return rows;
  }, [state.subforums, getPostsBySubforum]);

  // A search runs on the server once the API can (its rows carry `excerpt`):
  // one request over the selected space, or every space for 'All', matching
  // bodies the list no longer has to carry. A selected space goes to the
  // server too, or the 100-row cap fills with other spaces' matches and this
  // one comes back short. Until the first answer for a term arrives, and on an
  // API that cannot search, the loaded rows are filtered here as before. The
  // space filter and the sort apply to either.
  const activeSubforumId =
    activeCategory === 'All'
      ? undefined
      : state.subforums.find((sf) => sf.name === activeCategory)?.id;
  const searchResults = useServerPostSearch({
    query: searchQuery,
    enabled: searchesOnServer(allPosts),
    subforumId: activeSubforumId,
    sort,
    limit: ARCHIVE_SEARCH_LIMIT,
  });

  const filtered = useMemo(() => {
    let rows: PostListItem[] = searchResults ?? allPosts;
    if (activeCategory !== 'All') {
      rows = rows.filter(
        (p) => getSubforum(p.subforumId)?.name === activeCategory,
      );
    }
    const q = searchResults ? '' : forumSearchTerm(searchQuery).toLowerCase();
    if (q) {
      rows = rows.filter((p) => matchesSearch(p, q));
    }
    return [...rows].sort((a, b) =>
      sort === 'top'
        ? b.upvotes - a.upvotes
        : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [allPosts, searchResults, activeCategory, searchQuery, sort, getSubforum]);

  const isLoading =
    state.loading.subforums ||
    state.subforums.some((sf) => state.loading.posts.has(sf.id));

  const LINE = 'rgba(var(--ink-rgb),.13)';
  const MUTED = 'rgba(var(--ink-rgb),.62)';

  return (
    <section className="px-6 pb-20 pt-24">
      <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <nav
              className="mb-2 text-[11.5px] font-medium"
              style={{ color: MUTED }}
              aria-label="Breadcrumb"
            >
              <Link to="/forum" className="dc-h-cyan hover:underline">
                Forum
              </Link>
              <span className="px-1.5">/</span>
              <span style={{ color: 'var(--ink)' }}>Archive</span>
            </nav>
            <h1
              className="text-[30px] font-extrabold leading-[1.1] tracking-[-0.02em]"
              style={{ color: 'var(--ink)' }}
            >
              Archive
            </h1>
            <p className="mt-2 text-sm" style={{ color: MUTED }}>
              Every discussion across every space &mdash; {filtered.length}{' '}
              {filtered.length === 1 ? 'discussion' : 'discussions'}.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <div
            className="flex min-w-0 flex-1 basis-[220px] items-center gap-2.5 rounded-lg px-3.5 py-2.5"
            style={{ border: `1px solid ${LINE}`, background: 'var(--paper2)' }}
          >
            <Search size={15} className="flex-none" style={{ color: MUTED }} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search the archive"
              className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-xs focus-visible:ring-0"
              style={{ color: 'var(--ink)' }}
            />
          </div>

          <div
            className="flex gap-1 rounded-lg p-[3px]"
            style={{ background: 'rgba(var(--ink-rgb),.06)' }}
            role="group"
            aria-label="Sort the archive"
          >
            {(
              [
                { key: 'new', label: 'Recent' },
                { key: 'top', label: 'Top' },
              ] as const
            ).map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={sort === s.key}
                onClick={() => setSort(s.key)}
                className="dc-seg rounded-md px-3.5 py-[7px] text-xs font-semibold"
                data-active={sort === s.key ? '' : undefined}
              >
                {s.label}
              </button>
            ))}
          </div>

          <select
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
            aria-label="Filter by space"
            className="h-[38px] rounded-lg px-3 text-xs font-semibold"
            style={{
              border: `1px solid ${LINE}`,
              background: 'var(--paper2)',
              color: 'var(--ink)',
            }}
          >
            <option value="All">All spaces</option>
            {state.subforums.map((s) => (
              <option key={s.id} value={s.name}>
                {displaySpaceName(s.name)}
              </option>
            ))}
          </select>
        </div>

        <div
          className="overflow-hidden rounded-xl"
          style={{ border: `1px solid ${LINE}`, background: 'var(--paper2)' }}
        >
          {isLoading && allPosts.length === 0 ? (
            <div className="flex flex-col gap-4 p-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <ForumCardSkeleton key={i} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-14 text-center">
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                {searchQuery.trim()
                  ? 'No discussions match that search'
                  : 'Nothing in the archive yet'}
              </p>
              <p className="text-xs" style={{ color: MUTED }}>
                {searchQuery.trim()
                  ? 'Try a different term, or clear the space filter.'
                  : 'Discussions appear here as they are posted.'}
              </p>
            </div>
          ) : (
            filtered.map((post) => (
              <ThreadRow
                key={post.id}
                post={post}
                space={getSubforum(post.subforumId)}
                variant="index"
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

export default function ForumArchive() {
  return (
    <ForumProvider>
      <DcPage>
        <ForumArchiveContent />
      </DcPage>
    </ForumProvider>
  );
}
